import React from 'react';
import { EVENTS_THRESHOLD_TYPES } from '../../../../data/globalsettings';
import PropTypes from 'prop-types';

import { strings } from '../../../../localization';
import Switch from '../../../../components/common/Switch';
import ProgressOverlay from '../../../../components/common/ProgressOverlay';

import { UPDATE_SETTINGS_TIMEOUT } from '../GlobalSettings';

const EVENTS_SPEEDING_THRESHOLD_PERCENT_MAX = 100;
const EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_METRIC = 100;
const EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL = 62.2;

export default class EventsSpeeding extends React.PureComponent {

    static propTypes = {
        eventConfigAvailable: PropTypes.bool,
        eventAvailabilityPending: PropTypes.bool.isRequired,
        eventAvailabilityEnabled: PropTypes.bool,
        eventAvailabilityErrorMessage: PropTypes.string,
        applyEventAvailability: PropTypes.func.isRequired,
        timeoutEventAvailability: PropTypes.func.isRequired,

        eventThresholdPending: PropTypes.bool.isRequired,
        eventThreshold: PropTypes.number,
        eventThresholdType: PropTypes.string,
        eventThresholdErrorMessage: PropTypes.string,
        applyEventThreshold: PropTypes.func.isRequired,
        timeoutEventThreshold: PropTypes.func.isRequired,

        // distance units are used for providing the eventThresholdType CONSTANT as "km/h" or "mph"
        unitOfMeasureDistancePending: PropTypes.bool.isRequired,
        unitOfMeasureDistanceIsMetric: PropTypes.bool,
        unitOfMeasureDistanceErrorMessage: PropTypes.string,

        expandOrCollapseStateForUIReferences: PropTypes.func.isRequired,
        eventsSpeedingExpandReference: PropTypes.string.isRequired,
        eventsSpeedingExpandReferenceIsExpanded: PropTypes.bool.isRequired
    }

    constructor(props) {

        super(props);

        this.state = {
            eventThreshold: undefined,
            eventThresholdType: undefined
        }

        this.applyEventAvailabilityTimeoutId = undefined;
        this.applyEventThresholdTimeoutId = undefined;
    }

    componentWillUnmount() {
        clearTimeout(this.applyEventAvailabilityTimeoutId);
        clearTimeout(this.applyEventThresholdTimeoutId);
    }

    componentDidUpdate(prevProps) {
        // enforce EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL update on unitOfMeasure changes:
        if (prevProps.unitOfMeasureDistanceIsMetric !== this.props.unitOfMeasureDistanceIsMetric) {
            let eventThresholdType = this.props.eventThresholdType ? this.props.eventThresholdType : "";
            if (this.state.eventThresholdType !== undefined) {
                eventThresholdType = this.state.eventThresholdType ? this.state.eventThresholdType : "";
            }
            if (eventThresholdType === EVENTS_THRESHOLD_TYPES.CONSTANT) {
                if (this.state.eventThreshold && Number(this.state.eventThreshold) > EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL) {
                    this.setState({
                        eventThreshold: EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL
                    })
                }
            }
        }
    }

    discardEventsSpeedingThresholdChanges = () => {
        this.setState({
            eventThreshold: undefined,
            eventThresholdType: undefined
        });
    }

    applyEventThresholdState = () => {

        clearTimeout(this.applyEventThresholdTimeoutId);

        let eventThreshold = this.state.eventThreshold !== undefined ? this.state.eventThreshold : this.props.eventThreshold;
        const eventThresholdType = this.state.eventThresholdType !== undefined ? this.state.eventThresholdType : this.props.eventThresholdType;

        // validate this attempt to update speeding threshold
        if (isNaN(eventThreshold)) { return; }

        if (eventThresholdType === EVENTS_THRESHOLD_TYPES.CONSTANT) {
            if (this.props.unitOfMeasureDistanceIsMetric !== true) {
                eventThreshold = eventThreshold * 1.609;
                if (eventThreshold > EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_METRIC) {
                    eventThreshold = EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_METRIC;
                }
            }
        }
        eventThreshold = Math.round(eventThreshold); // rounding added for RAV-3502

        this.props.applyEventThreshold(
            Number(eventThreshold),
            eventThresholdType,
            this.props.eventThreshold,
            this.props.eventThresholdType
        )
        .then(
            () => {
                this.discardEventsSpeedingThresholdChanges();
                clearTimeout(this.applyEventThresholdTimeoutId);
            },
            () => {clearTimeout(this.applyEventThresholdTimeoutId)}
        )
    }

    handleThresholdChange = (event) => {

        const trimmedValue = event.target.value? event.target.value.trim() : null; // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)

        if (trimmedValue === null) {
            this.setState({
                eventThreshold: trimmedValue // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            });
            return;    
        }

        if (isNaN(trimmedValue)) { return; }

        const indexOfDecimal = trimmedValue.indexOf(".");
        if (indexOfDecimal !== -1) {
            if (indexOfDecimal < trimmedValue.length - 2) {// .01 || 0.01
                return; // .0 || 0.0
            }
        }

        let eventThresholdType = this.props.eventThresholdType ? this.props.eventThresholdType : ""; // RAV-1544 sanitizing "undefined"s becasue they will not clear values via state (discardWiFiCredentialsChanges() wouldn't work)
        if (this.state.eventThresholdType !== undefined) {
            eventThresholdType = this.state.eventThresholdType ? this.state.eventThresholdType : "";
        }

        if (eventThresholdType === EVENTS_THRESHOLD_TYPES.PERCENT) {
            if (Number(trimmedValue) > EVENTS_SPEEDING_THRESHOLD_PERCENT_MAX) { return; }
        } else {
            if (this.props.unitOfMeasureDistanceIsMetric !== true) {
                if (Number(trimmedValue) > EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL) { return; }
            } else {
                if (Number(trimmedValue) > EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_METRIC) { return; }
            }
        }

        const sanitizedNumberRoundedString = Number(trimmedValue).toFixed(1); // rounded to whole number
        const eventThresholdRoundedString = Number(this.props.eventThreshold).toFixed(1);

        if (sanitizedNumberRoundedString === eventThresholdRoundedString) {
            if (indexOfDecimal === -1 ||
                indexOfDecimal < trimmedValue.length - 1) { // allow trailing decimal during input
                this.setState({
                    eventThreshold: undefined
                });
                return;
            }
        }

        this.setState({
            eventThreshold: trimmedValue // allow trailing decimal during input
        });
    }

    handleThresholdTypeChange = (event) => {
        const newThresholdType = event.target.value;

        // enforce EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL update on threshold type changes:
        if (newThresholdType === EVENTS_THRESHOLD_TYPES.CONSTANT) {
            // switching from PERCENT to CONSTANT might push past EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL mph limit, if less than EVENTS_SPEEDING_THRESHOLD_PERCENT_MAX
            if (this.props.unitOfMeasureDistanceIsMetric !== true) {
                let eventThreshold = this.state.eventThreshold !== undefined ? this.state.eventThreshold : this.props.eventThreshold;
                if (Number(eventThreshold) > EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL) {
                    this.setState({
                        eventThreshold: EVENTS_SPEEDING_THRESHOLD_CONSTANT_MAX_IMPERIAL
                    })
                }
            }
        }
        this.setState({
            eventThresholdType: event.target.value
        });
    }

    render() {

        var showApplyButton = false;
        // if editing (if state version of ssid and password)
        if (this.state.eventThreshold !== undefined ||
            this.state.eventThresholdType !== undefined) {
            showApplyButton = true;
        }
        const formActionsClassName =  showApplyButton ? "form-actions" : "form-actions collapsed";

        let eventThreshold = ""; // RAV-1544 sanitizing "undefined"s becasue they will not clear values via state changes (discardWiFiCredentialsChanges() wouldn't work)
        if (this.props.eventThreshold !== undefined) {
            eventThreshold = this.props.eventThreshold;
        }
        if (this.state.eventThreshold !== undefined) { // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            eventThreshold = this.state.eventThreshold ? this.state.eventThreshold : "";
        }
        let eventThresholdType = this.props.eventThresholdType ? this.props.eventThresholdType : ""; // RAV-1544 sanitizing "undefined"s becasue they will not clear values via state (discardWiFiCredentialsChanges() wouldn't work)
        if (this.state.eventThresholdType !== undefined) {
            eventThresholdType = this.state.eventThresholdType ? this.state.eventThresholdType : "";
        }

        let speedingThresholdConstantName = this.props.unitOfMeasureDistanceIsMetric !== true ? strings.settings_global_events_speeding_threshold_type_constant_imperial : strings.settings_global_events_speeding_threshold_type_constant_metric;

        if (!this.props.eventConfigAvailable) {
            return null;
        }

        return (
            <div className='setting-group-horiz'>
                <div className={this.props.eventsSpeedingExpandReferenceIsExpanded ? "subgroup" : "subgroup collapsed"}>
                    <h5 className="subgroup-header">
                        <button disabled={true} // SUBSECTION_COLLAPSING_DISABLED
                            className={this.props.eventsSpeedingExpandReferenceIsExpanded ? "settings-disclosure-wrapper" : "settings-disclosure-wrapper collapsed"}
                            onClick={() => {
                                this.props.expandOrCollapseStateForUIReferences(
                                    !this.props.eventsSpeedingExpandReferenceIsExpanded,
                                    [this.props.eventsSpeedingExpandReference])
                            }}>
                            {strings.settings_global_events_speeding_header}
                            <i className="material-icons disclosure">keyboard_arrow_up</i>
                        </button>
                    </h5>
                    <h6 className="subgroup-subheader">
                        <div className='setting-group switch-group'>
                            <div>
                                <Switch
                                    onChange={() => {

                                        clearTimeout(this.applyEventAvailabilityTimeoutId);
                                        const eventAvailabilityEnabled = this.props.eventAvailabilityEnabled;
                                        this.applyEventAvailabilityTimeoutId = setTimeout(() => {this.props.timeoutEventAvailability(eventAvailabilityEnabled)}, UPDATE_SETTINGS_TIMEOUT);

                                        this.props.applyEventAvailability(!this.props.eventAvailabilityEnabled)
                                        .then(
                                            () => {clearTimeout(this.applyEventAvailabilityTimeoutId)},
                                            () => {clearTimeout(this.applyEventAvailabilityTimeoutId)}
                                        )
                                    }}
                                    checked={this.props.eventAvailabilityEnabled || false}
                                    disabled={this.props.eventAvailabilityPending}
                                    smallFormat={true}
                                />
                                <label disabled={this.props.eventAvailabilityPending}>{this.props.eventAvailabilityEnabled?
                                    strings.settings_global_events_speeding_are_enabled
                                :
                                    strings.settings_global_events_speeding_are_disabled
                                }</label>
                                {this.props.eventAvailabilityPending ? <ProgressOverlay /> : null }
                            </div>
                            <div className="error-message">
                                {this.props.eventAvailabilityErrorMessage}
                            </div>
                        </div>
                    </h6>
                    <div className='setting-group threshold speeding'>
                        <div className="description">
                            {strings.settings_global_events_speeding_threshold_description}
                        </div>
                        <div className="form">
                            <div>
                                <input type="text" autoComplete="off" className="threshold" value={eventThreshold} placeholder={strings.settings_global_events_speeding_threshold_placeholder} onChange={this.handleThresholdChange}></input>
                            </div>
                            <div>
                                <select name="speeding-threshold" value={eventThresholdType} onChange={this.handleThresholdTypeChange}>
                                    <option value={EVENTS_THRESHOLD_TYPES.CONSTANT}>{speedingThresholdConstantName}</option>
                                    <option value={EVENTS_THRESHOLD_TYPES.PERCENT}>{strings.settings_global_events_speeding_threshold_type_percent}</option>
                                </select>
                            </div>
                            <div>
                                <label className="inline-label-large">
                                    {strings.settings_global_events_speeding_threshold_label}
                                </label>
                            </div>
                        </div>
                        <div className={formActionsClassName}>
                            <button className="btn btn-blue" onClick={this.applyEventThresholdState}>{strings.action_apply}</button>
                            <button className="btn btn-outline-red" onClick={this.discardEventsSpeedingThresholdChanges}>{strings.settings_action_discard_changes}</button>
                        </div>
                        <div className="error-message form-indent">
                            {this.props.eventThresholdErrorMessage}
                        </div>
                        <div className="error-message form-indent">
                            {this.props.unitOfMeasureDistanceErrorMessage}
                        </div>
                        {this.props.eventThresholdPending || this.props.unitOfMeasureDistancePending? <ProgressOverlay /> : null }
                    </div>
                </div>
            </div>
        );
    }
}